"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";

interface SymbolStatisticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: string | null;
}

type ObservedSession = {
  bidHigh: number;
  bidLow: number;
  askHigh: number;
  askLow: number;
  openPrice: number;
  closePrice: number;
};

function StatRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-mono font-medium text-right tabular-nums">
        {value}
      </span>
    </div>
  );
}

export default function SymbolStatisticsDialog({
  open,
  onOpenChange,
  currency,
}: SymbolStatisticsDialogProps) {
  const entry = useSubscribedSymbolsStore((s) =>
    open && currency ? s.byCurrency.get(currency) : undefined
  );
  const quote = entry?.quote;
  const digits = entry?.symbol.digits ?? 5;

  const [observed, setObserved] = useState<ObservedSession | null>(null);

  useEffect(() => {
    if (!open) {
      setObserved(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !currency || !quote) return;

    setObserved((prev) => {
      const bid = quote.bid;
      const ask = quote.ask;
      const closeCandidate =
        quote.last > 0 ? quote.last : (bid + ask) / 2;

      if (!prev) {
        const openPx = closeCandidate;
        return {
          bidHigh: bid,
          bidLow: bid,
          askHigh: ask,
          askLow: ask,
          openPrice: openPx,
          closePrice: closeCandidate,
        };
      }

      return {
        bidHigh: Math.max(prev.bidHigh, bid),
        bidLow: Math.min(prev.bidLow, bid),
        askHigh: Math.max(prev.askHigh, ask),
        askLow: Math.min(prev.askLow, ask),
        openPrice: prev.openPrice,
        closePrice: closeCandidate,
      };
    });
  }, [
    open,
    currency,
    quote?.bid,
    quote?.ask,
    quote?.last,
    quote?.time,
  ]);

  const fmt = (n: number | undefined) =>
    n !== undefined && Number.isFinite(n) ? n.toFixed(digits) : "—";

  const bid = quote?.bid;
  const ask = quote?.ask;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-4">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg">
            {currency ?? "Symbol"} statistics
          </DialogTitle>
          <p className="text-xs text-muted-foreground font-normal pt-1">
            Session highs/lows and open/close are observed from this app while
            the dialog is open (not exchange session data).
          </p>
        </DialogHeader>
        <div className="mt-2">
          {!quote && (
            <p className="text-sm text-muted-foreground">Waiting for quote…</p>
          )}
          {quote && (
            <>
              <StatRow label="Bid" value={fmt(bid)} />
              <StatRow label="Bid High" value={fmt(observed?.bidHigh)} />
              <StatRow label="Bid Low" value={fmt(observed?.bidLow)} />
              <StatRow label="Ask" value={fmt(ask)} />
              <StatRow label="Ask High" value={fmt(observed?.askHigh)} />
              <StatRow label="Ask Low" value={fmt(observed?.askLow)} />
              <StatRow label="Open Price" value={fmt(observed?.openPrice)} />
              <StatRow label="Close Price" value={fmt(observed?.closePrice)} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
